import React, { useCallback, useEffect, useState } from "react";
import { OralModel } from "../../../../../models/Oral.model";
import {
    favoriteOral,
    favoriteOralDelete,
    getAllOralActive,
    putOral,
    saveClassification,
} from "../../../../../services/oralService";
import { handlePromise } from "../../../../../utils/handlePromisse";
import { getOralQuery, setOralQuery } from "../../../../../services/local-session-storage";
import { arrayWithoutOneElement, flexMultiFilter, orderObjectArray } from "../../../../../utils";

const FILTER_FIELDS = ["name", "classifications", "userClassifications", "annotation"];

/** Estado, fetch e handlers da etapa de seleção de ativos orais do wizard. */
export function useActivesOral(onFinish: () => void) {
    const model = getOralQuery();

    const [loading, setLoading] = useState(false);
    const [oralActives, setOralActives] = useState<OralModel[]>([]);
    const [value, setValue] = useState(0);
    const [filter, setFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [selectedOral, setSelectedOral] = useState<OralModel[]>(model.actives || []);
    const [activeOral, setActiveOral] = useState<OralModel>({} as OralModel);
    const [onPopUp, setOnPopUp] = useState(false);
    const [annotationOpen, setAnnotationOpen] = useState(false);
    const [classificationsOpen, setClassificationsOpen] = useState(false);
    const [activeSelected, setActiveSelected] = useState<OralModel | null>(null);
    const [text, setText] = useState<string[]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [filterKey, setFilterKey] = useState("name");
    const [classes, setClasses] = useState<string[]>([]);
    const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
    const [filteredAll, setFilteredAll] = useState<OralModel[]>([]);

    const fetchOral = useCallback(async () => {
        const [result, _] = await handlePromise(getAllOralActive());
        const data = result.data.data as OralModel[];
        setOralActives(data);

        const uniqueClasses = Array.from(new Set(data.flatMap((m) => m.classifications || [])));
        setClasses(uniqueClasses);
    }, []);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setFilter("");
        setValue(newValue);
    };

    const handleClickViewActive = (product: OralModel) => {
        setOpen(true);
        setActiveOral(product);
    };

    const handleFavorite = useCallback(
        async (product: OralModel) => {
            setLoading(true);
            try {
                let error: any;
                if (product.id) {
                    if (product.favorite == false) {
                        [, error] = await handlePromise(favoriteOral(product.id));
                    } else {
                        [, error] = await handlePromise(favoriteOralDelete(product.id));
                    }
                    if (error) {
                        setShowSnackbar(true);
                        const message = product.favorite == false ? "favoritar" : "desfavoritar";
                        setSnackbarMessage(`Erro ao ${message}`);
                        return;
                    }
                }
                await fetchOral();
                setSelectedOral((prevSelected) =>
                    prevSelected.map((item) => (item.id === product.id ? { ...item, favorite: !product.favorite } : item))
                );
            } finally {
                setLoading(false);
            }
        },
        [fetchOral, oralActives]
    );

    const handleSelectActive = useCallback(
        (product: OralModel, checked: boolean) => {
            if (!checked) {
                const index = selectedOral.findIndex((prod) => prod.id === product.id);
                setSelectedOral(arrayWithoutOneElement(selectedOral, index));
                const indexQuery = model.actives.findIndex((prod) => prod.id === product.id);
                setOralQuery({ actives: arrayWithoutOneElement(model.actives, indexQuery) });
            } else {
                setSelectedOral([...selectedOral, product]);
                setOralQuery({ actives: [...selectedOral, product] });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedOral]
    );

    const handleContinue = () => {
        setOralQuery({ actives: selectedOral });
        onFinish();
    };

    const handleClickOpen = useCallback(async (active: OralModel) => {
        setActiveSelected(active);
        setAnnotationOpen(true);
    }, []);

    const handleClassification = useCallback(async (active: OralModel) => {
        if (active.userClassifications) {
            setText(active.userClassifications);
        }
        setActiveSelected(active);
        setClassificationsOpen(true);
    }, []);

    const popUpOpen = useCallback(async () => {
        setOnPopUp(true);
    }, []);

    const handleClose = () => {
        setOnPopUp(false);
    };

    const saveAnnotation = useCallback(
        async (newAnnotation: string) => {
            if (!activeSelected) return;
            if (!activeSelected.id) return;
            const annotation = newAnnotation;

            const [_, error] = await handlePromise(putOral(activeSelected?.id, annotation));
            if (!!error) {
                return;
            }

            fetchOral();
            setAnnotationOpen(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeSelected]
    );

    const classificationSave = useCallback(
        async (newClassification: string[]) => {
            if (!activeSelected) return;
            if (!activeSelected.id) return;

            if (activeSelected.userClassifications) {
                const [_, error] = await handlePromise(saveClassification(activeSelected?.id, newClassification));
                if (!!error) {
                    setShowSnackbar(true);
                    setSnackbarMessage("Erro ao salvar Classificações");
                    return;
                }
            }

            fetchOral();
            setClassificationsOpen(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeSelected]
    );

    const deleteClassification = useCallback(
        async (classification: string, active: OralModel) => {
            setActiveSelected(active);

            if (active?.userClassifications) {
                const updatedClassifications = active.userClassifications.filter((chip) => chip !== classification);

                if (!active.id) return;

                const [_, error] = await handlePromise(saveClassification(active.id, updatedClassifications));
                if (!!error) {
                    setShowSnackbar(true);
                    setSnackbarMessage("Erro ao deletar as Classificações");
                    return;
                }
            }

            fetchOral();
            setClassificationsOpen(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleSelectedClasses = (next: string[]) => {
        setSelectedClasses(next);
    };

    useEffect(() => {
        fetchOral();
    }, [fetchOral]);

    useEffect(() => {
        const filteredAllList = orderObjectArray(
            flexMultiFilter(oralActives, FILTER_FIELDS, filter).filter(
                (item) =>
                    selectedClasses.length === 0 ||
                    item.classifications.some((classification) => selectedClasses.includes(classification))
            )
        )
            .by("name")
            .asc();

        setFilteredAll(filteredAllList);
    }, [filter, selectedClasses, oralActives, selectedOral]);

    /** Lista filtrada+ordenada para uma aba secundária (favoritos/selecionados). */
    const orderedFiltered = (source: OralModel[]) =>
        orderObjectArray(flexMultiFilter(source, FILTER_FIELDS, filter)).by("name").asc();

    const favorites = oralActives.filter((oral) => oral.favorite === true);
    const selectedCount = selectedOral.length;

    return {
        loading,
        oralActives,
        value,
        filter,
        setFilter,
        open,
        setOpen,
        selectedOral,
        activeOral,
        onPopUp,
        annotationOpen,
        setAnnotationOpen,
        classificationsOpen,
        setClassificationsOpen,
        activeSelected,
        text,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        filterKey,
        setFilterKey,
        classes,
        selectedClasses,
        filteredAll,
        favorites,
        selectedCount,
        handleChange,
        handleClickViewActive,
        handleFavorite,
        handleSelectActive,
        handleContinue,
        handleClickOpen,
        handleClassification,
        popUpOpen,
        handleClose,
        saveAnnotation,
        classificationSave,
        deleteClassification,
        handleSelectedClasses,
        orderedFiltered,
    };
}
